import { z } from 'zod';

import type { TFunction } from 'i18next';

import { imageFieldSchema } from './common';

const numericSchema = (t: TFunction) =>
  z.string().refine((value) => !isNaN(Number(value)), {
    message: t('validation_invalid'),
  });

const kitItemFormSchema = (t: TFunction) =>
  z.object({
    id: z.number().optional(),
    product: z
      .object({
        id: z.number(),
        name: z.string(),
        input_unit: z.string().nullable(),
      })
      .nullable(),
    quantity: numericSchema(t),
  });

type KitItemFormValues = z.infer<ReturnType<typeof kitItemFormSchema>>;

type ProductFieldValues = {
  kind: string;
  input_unit: string;
  package_size: string;
  sold_by_portion: boolean;
  portion_size: string;
  portion_price: string;
  consignment: boolean;
  supplier_id: string | null;
  supplier_price: string;
};

const refineKitFields =
  (t: TFunction) =>
  (
    values: { kind: string; kit_items: KitItemFormValues[] },
    ctx: z.RefinementCtx,
  ) => {
    if (values.kind !== 'kit') {
      return;
    }

    values.kit_items.forEach((item, index) => {
      if (!item.product) {
        ctx.addIssue({
          code: 'custom',
          path: ['kit_items', index, 'product'],
          message: t('validation_required'),
        });
      }

      if (!(Number(item.quantity) > 0)) {
        ctx.addIssue({
          code: 'custom',
          path: ['kit_items', index, 'quantity'],
          message: t('validation_required'),
        });
      }
    });
  };

const refineProductFields =
  (t: TFunction) => (values: ProductFieldValues, ctx: z.RefinementCtx) => {
    if (values.kind !== 'product') {
      return;
    }

    const requiredFields = [
      'package_size',
      'input_unit',
      ...(values.sold_by_portion
        ? (['portion_size', 'portion_price'] as const)
        : []),
      ...(values.consignment
        ? (['supplier_id', 'supplier_price'] as const)
        : []),
    ] as const;

    requiredFields
      .filter((field) => !values[field])
      .forEach((field) =>
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message: t('validation_required'),
        }),
      );
  };

export function createInventoryProductFormSchema(t: TFunction) {
  const required = { message: t('validation_required') };

  return z
    .object({
      kind: z.enum(['product', 'kit']),
      name: z.string(required).trim().min(1, required),
      category_id: z.string(required).min(1, required),
      business_direction_id: z.string(required).min(1, required),
      sku: z.string(),
      description: z.string(),
      image: imageFieldSchema.nullable(),
      input_unit: z.string(),
      package_size: numericSchema(t),
      package_price: numericSchema(t),
      sold_by_portion: z.boolean(),
      portion_size: numericSchema(t),
      portion_price: numericSchema(t),
      critical_quantity: numericSchema(t),
      consignment: z.boolean(),
      supplier_id: z.string().nullable(),
      supplier_price: numericSchema(t),
      kit_items: z.array(kitItemFormSchema(t)),
    })
    .superRefine(refineKitFields(t))
    .superRefine(refineProductFields(t));
}

export type InventoryProductFormValues = z.infer<
  ReturnType<typeof createInventoryProductFormSchema>
>;

export type InventoryProductKitItemFormValues =
  InventoryProductFormValues['kit_items'][number];
